// This Source Code Form is subject to the terms of the Mozilla Public
// License, v. 2.0. If a copy of the MPL was not distributed with this
// file, You can obtain one at https://mozilla.org/MPL/2.0/.
// Units setting: altitude/speed units persisted in localStorage (spec §2:
// "units (ft/m, kt/km/h), stored in localStorage only").
export type AltitudeUnit = 'ft' | 'm';
export type SpeedUnit = 'kt' | 'kmh';

export interface UnitSettings {
  altitude: AltitudeUnit;
  speed: SpeedUnit;
}

export const DEFAULT_UNITS: UnitSettings = { altitude: 'ft', speed: 'kt' };

// Session 25d: when the visitor has no saved choice, the default unit system
// derives from the browser language. One small table (primary subtag ->
// unit system) so more languages can be added later; nl is metric like the
// rest of Dutch life, every other language keeps the aviation ft/kt default.
const LANGUAGE_UNIT_DEFAULTS: Readonly<Record<string, UnitSettings>> = {
  nl: { altitude: 'm', speed: 'kmh' },
};

/** Primary subtag of a BCP-47 language tag, lowercased ("nl-BE" -> "nl") */
export function primarySubtag(tag: string): string {
  const base = tag.split('-')[0] ?? '';
  return base.toLowerCase();
}

/** Browser-language-derived default units (session 25d): the first entry of
 *  navigator.languages (falling back to navigator.language) whose primary
 *  subtag has a table entry wins; otherwise ft/kt. Never persisted. */
export function defaultUnitsForLanguages(languages: readonly string[] | undefined): UnitSettings {
  for (const tag of languages ?? []) {
    if (typeof tag !== 'string' || tag === '') continue;
    const mapped = LANGUAGE_UNIT_DEFAULTS[primarySubtag(tag)];
    if (mapped) return mapped;
  }
  return DEFAULT_UNITS;
}

/** The browser's Accept-Language-ish list as one array (test seam) */
function browserLanguages(): readonly string[] {
  const langs = typeof navigator !== 'undefined' ? navigator.languages : undefined;
  if (langs && langs.length > 0) return langs;
  const lang = typeof navigator !== 'undefined' ? navigator.language : undefined;
  return lang ? [lang] : [];
}

const STORAGE_KEY_UNITS = 'vliegvuil.units';

/** Read persisted units; a saved choice always wins. With no saved value the
 *  default derives from the browser language (session 25d) and is NOT
 *  persisted — a later language change must be able to change the default. */
export function loadUnits(): UnitSettings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_UNITS);
    if (raw === null) return defaultUnitsForLanguages(browserLanguages());
    const parsed = JSON.parse(raw) as Partial<UnitSettings>;
    return {
      altitude: parsed.altitude === 'm' ? 'm' : 'ft',
      speed: parsed.speed === 'kmh' ? 'kmh' : 'kt',
    };
  } catch {
    // localStorage unavailable (blocked/corrupt): the language-derived
    // default still applies.
    return defaultUnitsForLanguages(browserLanguages());
  }
}

/** Persist units; failures are ignored (setting still applies for this session) */
export function saveUnits(units: UnitSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY_UNITS, JSON.stringify(units));
  } catch {
    // Ignore persistence failures
  }
}

const FT_PER_M = 3.28084;
const KM_PER_NM = 1.852;

/** Format an altitude in feet as the chosen unit */
export function formatAltitude(altFt: number | null, unit: AltitudeUnit): string {
  if (altFt === null) return '—';
  if (unit === 'm') return `${Math.round(altFt / FT_PER_M).toLocaleString()} m`;
  return `${Math.round(altFt).toLocaleString()} ft`;
}

/** Format a speed in knots as the chosen unit */
export function formatSpeed(speedKt: number | null, unit: SpeedUnit): string {
  if (speedKt === null) return '—';
  if (unit === 'kmh') return `${Math.round(speedKt * KM_PER_NM)} km/h`;
  return `${Math.round(speedKt)} kt`;
}
